import makeStyles from '@mui/styles/makeStyles';
import classNames from "classnames";
import {Check} from "lucide-react";
import {filter, find, findIndex, get, isEmpty, map} from 'lodash';
import React, {useEffect} from "react";
import {useTranslation} from "react-i18next";
import {useParams} from "react-router-dom";
import {history} from "common/history";
import {convertUrl} from "common/helpers";
import {color, font, radius, space, type} from "common/tokens";
import useFreshData, {dataKeyFor, dataKeys} from "common/useFreshData";
import {useSetTranslation} from "common/useSetTranslation";
import CtaButton from "components/redesign/CtaButton";
import ShimmerImage from "components/redesign/ShimmerImage";
import Skeleton from "components/redesign/Skeleton";

const stageStates = {
  DONE: 'done',
  CURRENT: 'current',
  LOCKED: 'locked',
};

const useStyles = makeStyles({
  page: {
    background: color.SURFACE,
    minHeight: '100%',
  },
  container: {
    maxWidth: space.CONTAINER_MAX,
    margin: '0 auto',
    padding: '0 32px',
  },
  back: {
    ...type.body,
    fontSize: 16,
    fontWeight: font.MEDIUM,
    color: color.TEXT_MUTED,
    appearance: 'none',
    background: 'none',
    border: 'none',
    padding: '11px 0',
    cursor: 'pointer',
  },
  content: {
    width: 638,
    maxWidth: '100%',
    margin: '0 auto',
    padding: '34px 0 80px',
    display: 'flex',
    flexDirection: 'column',
  },
  title: {
    ...type.pageTitle,
    color: color.TEXT,
    margin: 0,
  },
  // Width-led: the banner takes the column and keeps its own ratio, so nothing
  // of it is clipped whatever shape it was uploaded in.
  hero: {
    width: '100%',
    borderRadius: radius.PANEL,
    marginTop: 24.7,
  },
  description: {
    ...type.body,
    color: color.TEXT_MUTED,
    margin: '20px 0 0',
  },
  stagesLabel: {
    ...type.eyebrow,
    fontWeight: font.BOLD,
    letterSpacing: '1.43px',
    color: color.SECTION_TITLE,
    margin: '28px 0 10px',
  },
  stages: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  // The stage the reader is on is filled so the list says where the survey
  // resumes; the ones after it are greyed because they are not open yet.
  stage: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    border: `1px solid ${color.CARD_BORDER}`,
    borderRadius: radius.CONTROL,
    padding: 14,
    background: color.SURFACE,
  },
  stageCurrent: {
    borderColor: color.ACCENT,
    background: color.ACCENT_TINT,
  },
  stageLocked: {
    background: color.SURFACE_MUTED,
  },
  badge: {
    ...type.meta,
    width: 26,
    height: 26,
    flexShrink: 0,
    borderRadius: radius.PILL,
    background: color.SURFACE_MUTED,
    color: color.TEXT_MUTED,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeDone: {
    background: color.SUCCESS,
    color: color.SURFACE,
  },
  badgeCurrent: {
    background: color.ACCENT,
    color: color.ON_ACCENT,
  },
  stageBody: {
    minWidth: 0,
  },
  stageName: {
    ...type.cardHeading,
    fontSize: 15,
    color: color.TEXT,
    margin: 0,
  },
  stageNameLocked: {
    color: color.TEXT_MUTED,
  },
  stageDescription: {
    ...type.meta,
    fontWeight: font.REGULAR,
    color: color.TEXT_MUTED,
    margin: '4px 0 0',
  },
  actions: {
    display: 'flex',
    paddingTop: 24,
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 16,
  },
  // Stands in for the banner while the survey is on its way.
  heroSkeleton: {
    marginTop: 8.7,
    borderRadius: radius.PANEL,
  },
});

export default function SurveyLandingPage({
  location,
  userSurveys,
  initSurvey,
  getAllSurveys,
  survey,
  surveys,
  setCurrentPath,
}) {
  const classes = useStyles();
  const {t} = useTranslation();
  const {st} = useSetTranslation();
  const {id} = useParams();
  const surveyId = get(location, 'state.surveyId') ?? id;

  useEffect(() => {
    setCurrentPath('survey');
  }, [setCurrentPath]);

  useFreshData(
    dataKeyFor(dataKeys.SURVEY, surveyId),
    () => initSurvey(surveyId),
    {enabled: Boolean(surveyId)}
  );
  // Carries the description, and is already cached when the reader came through
  // the listing.
  useFreshData(dataKeys.SURVEYS, getAllSurveys);

  const ready = Boolean(userSurveys) && String(get(survey, 'id')) === String(surveyId);

  const userSurvey = get(filter(userSurveys, s => s.surveyId === surveyId), '[0]', null);
  const surveyStatus = get(userSurvey, 'surveyStatus', '');
  const isSurveyDone = surveyStatus.startsWith('COMPLETE') || surveyStatus === 'APPROVED';

  const currentStageId = get(userSurvey, 'currentStageId');
  const stages = get(survey, 'stages', []);
  const currentIndex = Math.max(findIndex(stages, stage => stage.id === currentStageId), 0);
  // A finished stage stays COMPLETED until the gap to the next one elapses.
  const isCurrentStageDone = get(userSurvey, 'stageStatus') === 'COMPLETED';

  const stageStateAt = index => {
    if (isSurveyDone || index < currentIndex) {
      return stageStates.DONE;
    }
    if (index > currentIndex) {
      return stageStates.LOCKED;
    }
    return isCurrentStageDone ? stageStates.DONE : stageStates.CURRENT;
  };

  const background = get(survey, 'backgroundImage');
  const hero = isEmpty(background) ? '' : convertUrl(background);

  const surveySummary = find(surveys, item => item.id === surveyId);
  const description = st(
    get(surveySummary, 'shortDescription'),
    get(surveySummary, 'localeShortDescription')
  );

  // Nothing to act on while the reader is between stages or the survey is over,
  // so the button is withheld rather than offered and then refused.
  const hasActionableStage =
    !isEmpty(stages) && stageStateAt(currentIndex) === stageStates.CURRENT;

  const ctaLabel = currentIndex > 0
    ? t('continueSurveyCaps')
    : surveyStatus.startsWith('PARTIAL') || surveyStatus === 'ONGOING'
      ? t('resumeSurveyCaps')
      : t('startSurveyCaps');

  return (
    <div className={classes.page}>
      <div className={classes.container}>
        <button type={'button'} className={classes.back} onClick={() => history.push('/surveylisting')}>
          {`← ${t('survey')}`}
        </button>

        <div className={classes.content}>
          {!ready ? (
            <div className={classes.loading}>
              <Skeleton width={280} height={37}/>
              <Skeleton height={260} className={classes.heroSkeleton}/>
              <Skeleton width={'80%'} height={20}/>
              <Skeleton height={70}/>
              <Skeleton height={70}/>
            </div>
          ) : (
            <>
              <h1 className={classes.title}>{st(get(survey, 'name'), get(survey, 'localeName'))}</h1>

              {Boolean(hero) && (
                <ShimmerImage src={hero} fit={'auto'} placeholderHeight={260} className={classes.hero}/>
              )}

              {Boolean(description) && <p className={classes.description}>{description}</p>}

              {!isEmpty(stages) && (
                <>
                  <p className={classes.stagesLabel}>{t('surveyStages')}</p>
                  <ul className={classes.stages}>
                    {map(stages, (stage, index) => {
                      const state = stageStateAt(index);
                      const isDone = state === stageStates.DONE;
                      const isCurrent = state === stageStates.CURRENT;
                      const isLocked = state === stageStates.LOCKED;
                      const stageDescription = st(stage.description, stage.localeDescription);
                      return (
                        <li
                          key={stage.id}
                          className={classNames(
                            classes.stage,
                            isCurrent && classes.stageCurrent,
                            isLocked && classes.stageLocked
                          )}
                        >
                          <span
                            className={classNames(
                              classes.badge,
                              isDone && classes.badgeDone,
                              isCurrent && classes.badgeCurrent
                            )}
                          >
                            {isDone ? <Check size={14} strokeWidth={3}/> : index + 1}
                          </span>
                          <span className={classes.stageBody}>
                            <p
                              className={classNames(
                                classes.stageName,
                                isLocked && classes.stageNameLocked
                              )}
                            >
                              {st(stage.name, stage.localeName)}
                            </p>
                            {Boolean(stageDescription) && (
                              <p className={classes.stageDescription}>{stageDescription}</p>
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}

              {hasActionableStage && (
                <div className={classes.actions}>
                  <CtaButton
                    size={'large'}
                    label={ctaLabel}
                    onClick={() => history.push('/stage', {stageId: currentStageId, surveyId})}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
